import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./ConsolePage.module.css";
import LiveMapPage from "../LiveMap/LiveMapPage";
import VehicleModelsPage from "../VehicleModels/VehicleModelsPage";
import VehiclesPage from "../Vehicles/VehiclesPage";
import VendorsPage from "../Vendors/VendorsPage";
import DriversPage from "../Drivers/DriversPage";
import ParkingPage from "../Parking/ParkingPage";
import DriverMessagesPage from "../DriverMessages/DriverMessagesPage";
import DashboardPage from "../Dashboard/DashboardPage";

export default function ConsolePage() {
  const { currentUser, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Track which sections are expanded
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    fleet: false,
    clients: false,
    reports: false,
  });

  // Get current view from URL
  const getCurrentView = () => {
    const path = location.pathname;
    if (path === "/console" || path === "/console/") return "dashboard";
    return path.replace("/console/", "");
  };

  const currentView = getCurrentView();

  // Auto-expand sections based on current route
  useEffect(() => {
    const fleetPaths = ["vehicle-models", "vehicles", "vendors", "drivers", "parking", "driver-messages", "shifts"];
    const clientsPaths = ["clients", "client-messages", "business-accounts", "pricing"];
    const reportsPaths = ["reports-overview", "reports-drivers", "reports-capacity", "reports-customers"];

    if (fleetPaths.includes(currentView)) {
      setExpandedSections(prev => ({ ...prev, fleet: true }));
    } else if (clientsPaths.includes(currentView)) {
      setExpandedSections(prev => ({ ...prev, clients: true }));
    } else if (reportsPaths.includes(currentView)) {
      setExpandedSections(prev => ({ ...prev, reports: true }));
    }
  }, [currentView]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const navigateTo = (path: string) => {
    if (path === "dashboard") {
      navigate("/console");
    } else {
      navigate(`/console/${path}`);
    }
  };

  // If Live Map is selected, show the Live Map page with dark sidebar
  if (currentView === "livemap") {
    return (
      <div className={styles.liveMapLayout}>
        {/* Dark Sidebar for Live Map */}
        <aside className={styles.darkSidebar}>
          <div className={styles.sidebarBrand}>
            <div className={styles.brandIcon}></div>
            <div className={styles.brandName}>RideSpace</div>
          </div>
          
          <nav className={styles.darkNav}>
            {/* Dashboard */}
            <button onClick={() => navigateTo("dashboard")} className={styles.darkNavItem}>
              <span className={styles.navIcon}></span>
              <span>Dashboard</span>
            </button>

            {/* Fleet Section */}
            <div className={styles.navSection}>
              <button 
                onClick={() => toggleSection('fleet')} 
                className={styles.darkNavSection}
              >
                <span className={styles.navIcon}></span>
                <span>Fleet</span>
                <span className={`${styles.expandIcon} ${expandedSections.fleet ? styles.expandIconOpen : ''}`}>▶</span>
              </button>
              {expandedSections.fleet && (
                <div className={styles.darkSubNav}>
                  <button onClick={() => navigateTo("vehicle-models")} className={styles.darkSubNavItem}>
                    Vehicle Models
                  </button>
                  <button onClick={() => navigateTo("vehicles")} className={styles.darkSubNavItem}>
                    Vehicles
                  </button>
                  <button onClick={() => navigateTo("vendors")} className={styles.darkSubNavItem}>
                    Vendors
                  </button>
                  <button onClick={() => navigateTo("drivers")} className={styles.darkSubNavItem}>
                    Drivers
                  </button>
                  <button onClick={() => navigateTo("parking")} className={styles.darkSubNavItem}>
                    Parking
                  </button>
                  <button onClick={() => navigateTo("driver-messages")} className={styles.darkSubNavItem}>
                    Driver Messages
                  </button>
                  <button onClick={() => navigateTo("shifts")} className={styles.darkSubNavItem}>
                    Shifts
                  </button>
                </div>
              )}
            </div>

            {/* Clients Section */}
            <div className={styles.navSection}>
              <button 
                onClick={() => toggleSection('clients')} 
                className={styles.darkNavSection}
              >
                <span className={styles.navIcon}></span>
                <span>Clients</span>
                <span className={`${styles.expandIcon} ${expandedSections.clients ? styles.expandIconOpen : ''}`}>▶</span>
              </button>
              {expandedSections.clients && (
                <div className={styles.darkSubNav}>
                  <button onClick={() => navigateTo("clients")} className={styles.darkSubNavItem}>
                    Clients
                  </button>
                  <button onClick={() => navigateTo("client-messages")} className={styles.darkSubNavItem}>
                    Client Messages
                  </button>
                  <button onClick={() => navigateTo("business-accounts")} className={styles.darkSubNavItem}>
                    Business Accounts
                  </button>
                  <button onClick={() => navigateTo("pricing")} className={styles.darkSubNavItem}>
                    Pricing
                  </button>
                </div>
              )}
            </div>

            {/* Planning */}
            <button onClick={() => navigateTo("planning")} className={styles.darkNavItem}>
              <span className={styles.navIcon}></span>
              <span>Planning</span>
            </button>

            {/* Reports Section */}
            <div className={styles.navSection}>
              <button 
                onClick={() => toggleSection('reports')} 
                className={styles.darkNavSection}
              >
                <span className={styles.navIcon}></span>
                <span>Reports</span>
                <span className={`${styles.expandIcon} ${expandedSections.reports ? styles.expandIconOpen : ''}`}>▶</span>
              </button>
              {expandedSections.reports && (
                <div className={styles.darkSubNav}>
                  <button onClick={() => navigateTo("reports-overview")} className={styles.darkSubNavItem}>
                    Overview
                  </button>
                  <button onClick={() => navigateTo("reports-drivers")} className={styles.darkSubNavItem}>
                    Drivers
                  </button>
                  <button onClick={() => navigateTo("reports-capacity")} className={styles.darkSubNavItem}>
                    Capture Max Capacity
                  </button>
                  <button onClick={() => navigateTo("reports-customers")} className={styles.darkSubNavItem}>
                    Customers
                  </button>
                </div>
              )}
            </div>

            {/* Live Map - Active */}
            <button onClick={() => navigateTo("livemap")} className={`${styles.darkNavItem} ${styles.darkNavItemActive}`}>
              <span className={styles.navIcon}></span>
              <span>Live Map</span>
            </button>
          </nav>
        </aside>

        {/* Live Map Content */}
        <div className={styles.liveMapContent}>
          <LiveMapPage />
        </div>
      </div>
    );
  }

  // Default Dashboard View with Light Sidebar
  return (
    <div className={styles.page}>
      {/* Light Sidebar */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarBrand}>
          <div className={styles.brandIcon}></div>
          <div className={styles.topBarLeft}>
            <img src="/ridebase-logo.svg" alt="Ridebase" className={styles.logo} />
          </div>
        </div>
        
        <nav className={styles.nav}>
          {/* Dashboard */}
          <button 
            onClick={() => navigateTo("dashboard")} 
            className={`${styles.navItem} ${currentView === "dashboard" ? styles.navItemActive : ""}`}
          >
            <span className={styles.navIcon}></span>
            <span>Dashboard</span>
          </button>

          {/* Fleet Section */}
          <div className={styles.navSection}>
            <button 
              onClick={() => toggleSection('fleet')} 
              className={styles.navSectionBtn}
            >
              <span className={styles.navIcon}></span>
              <span>Fleet</span>
              <span className={`${styles.expandIcon} ${expandedSections.fleet ? styles.expandIconOpen : ''}`}>▶</span>
            </button>
            {expandedSections.fleet && (
              <div className={styles.subNav}>
                <button 
                  onClick={() => navigateTo("vehicle-models")} 
                  className={`${styles.subNavItem} ${currentView === "vehicle-models" ? styles.subNavItemActive : ""}`}
                >
                  Vehicle Models
                </button>
                <button 
                  onClick={() => navigateTo("vehicles")} 
                  className={`${styles.subNavItem} ${currentView === "vehicles" ? styles.subNavItemActive : ""}`}
                >
                  Vehicles
                </button>
                <button 
                  onClick={() => navigateTo("vendors")} 
                  className={`${styles.subNavItem} ${currentView === "vendors" ? styles.subNavItemActive : ""}`}
                >
                  Vendors
                </button>
                <button 
                  onClick={() => navigateTo("drivers")} 
                  className={`${styles.subNavItem} ${currentView === "drivers" ? styles.subNavItemActive : ""}`}
                >
                  Drivers
                </button>
                <button 
                  onClick={() => navigateTo("parking")} 
                  className={`${styles.subNavItem} ${currentView === "parking" ? styles.subNavItemActive : ""}`}
                >
                  Parking
                </button>
                <button 
                  onClick={() => navigateTo("driver-messages")} 
                  className={`${styles.subNavItem} ${currentView === "driver-messages" ? styles.subNavItemActive : ""}`}
                >
                  Driver Messages
                </button>
                <button 
                  onClick={() => navigateTo("shifts")} 
                  className={`${styles.subNavItem} ${currentView === "shifts" ? styles.subNavItemActive : ""}`}
                >
                  Shifts
                </button>
              </div>
            )}
          </div>

          {/* Clients Section */}
          <div className={styles.navSection}>
            <button 
              onClick={() => toggleSection('clients')} 
              className={styles.navSectionBtn}
            >
              <span className={styles.navIcon}></span>
              <span>Clients</span>
              <span className={`${styles.expandIcon} ${expandedSections.clients ? styles.expandIconOpen : ''}`}>▶</span>
            </button>
            {expandedSections.clients && (
              <div className={styles.subNav}>
                <button 
                  onClick={() => navigateTo("clients")} 
                  className={`${styles.subNavItem} ${currentView === "clients" ? styles.subNavItemActive : ""}`}
                >
                  Clients
                </button>
                <button 
                  onClick={() => navigateTo("client-messages")} 
                  className={`${styles.subNavItem} ${currentView === "client-messages" ? styles.subNavItemActive : ""}`}
                >
                  Client Messages
                </button>
                <button 
                  onClick={() => navigateTo("business-accounts")} 
                  className={`${styles.subNavItem} ${currentView === "business-accounts" ? styles.subNavItemActive : ""}`}
                >
                  Business Accounts
                </button>
                <button 
                  onClick={() => navigateTo("pricing")} 
                  className={`${styles.subNavItem} ${currentView === "pricing" ? styles.subNavItemActive : ""}`}
                >
                  Pricing
                </button>
              </div>
            )}
          </div>

          {/* Planning */}
          <button 
            onClick={() => navigateTo("planning")} 
            className={`${styles.navItem} ${currentView === "planning" ? styles.navItemActive : ""}`}
          >
            <span className={styles.navIcon}></span>
            <span>Planning</span>
          </button>

          {/* Reports Section */}
          <div className={styles.navSection}>
            <button 
              onClick={() => toggleSection('reports')} 
              className={styles.navSectionBtn}
            >
              <span className={styles.navIcon}></span>
              <span>Reports</span>
              <span className={`${styles.expandIcon} ${expandedSections.reports ? styles.expandIconOpen : ''}`}>▶</span>
            </button>
            {expandedSections.reports && (
              <div className={styles.subNav}>
                <button 
                  onClick={() => navigateTo("reports-overview")} 
                  className={`${styles.subNavItem} ${currentView === "reports-overview" ? styles.subNavItemActive : ""}`}
                >
                  Overview
                </button>
                <button 
                  onClick={() => navigateTo("reports-drivers")} 
                  className={`${styles.subNavItem} ${currentView === "reports-drivers" ? styles.subNavItemActive : ""}`}
                >
                  Drivers
                </button>
                <button 
                  onClick={() => navigateTo("reports-capacity")} 
                  className={`${styles.subNavItem} ${currentView === "reports-capacity" ? styles.subNavItemActive : ""}`}
                >
                  Capture Max Capacity
                </button>
                <button 
                  onClick={() => navigateTo("reports-customers")} 
                  className={`${styles.subNavItem} ${currentView === "reports-customers" ? styles.subNavItemActive : ""}`}
                >
                  Customers
                </button>
              </div>
            )}
          </div>

          {/* Live Map */}
          <button 
            onClick={() => navigateTo("livemap")} 
            className={styles.navItem}
          >
            <span className={styles.navIcon}></span>
            <span>Live Map</span>
          </button>
        </nav>
      </aside>

      {/* Main Content Area */}
      <div className={styles.mainArea}>
        {/* Top Bar */}
        <header className={styles.topBar}>
          <div className={styles.topBarLeft}>
            <h1 className={styles.pageTitle}>
              {currentView === "dashboard" && "Dashboard"}
              {currentView === "vehicle-models" && "Vehicle Models"}
              {currentView === "vehicles" && "Vehicles"}
              {currentView === "vendors" && "Vendors"}
              {currentView === "drivers" && "Drivers"}
              {currentView === "parking" && "Parking"}
              {currentView === "driver-messages" && "Driver Messages"}
              {currentView === "shifts" && "Shifts"}
              {currentView === "clients" && "Clients"}
              {currentView === "client-messages" && "Client Messages"}
              {currentView === "business-accounts" && "Business Accounts"}
              {currentView === "pricing" && "Pricing"}
              {currentView === "planning" && "Planning"}
              {currentView === "reports-overview" && "Reports - Overview"}
              {currentView === "reports-drivers" && "Reports - Drivers"}
              {currentView === "reports-capacity" && "Reports - Max Capacity"}
              {currentView === "reports-customers" && "Reports - Customers"}
            </h1>
          </div>
          
          <div className={styles.topBarRight}>
            <button className={styles.inviteBtn}>
              Invite Team Members
            </button>
            <button className={styles.upgradeBtn}>Upgrade Plan</button>
            <div className={styles.userMenu}>
              <span className={styles.userEmail}>{currentUser?.email}</span>
              <button onClick={handleSignOut} className={styles.signOutBtn}>
                Sign Out
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className={styles.content}>
          {currentView === "dashboard" && (
            <DashboardPage />
          )}
          {currentView === "vehicle-models" && (
            <VehicleModelsPage />
          )}

          {currentView === "vehicles" && (
            <VehiclesPage />
          )}

          {currentView === "vendors" && (
            <VendorsPage />
          )}

          {currentView === "drivers" && (
            <DriversPage />
          )}

          {currentView === "parking" && (
            <ParkingPage />
          )}

          {currentView === "driver-messages" && (
            <DriverMessagesPage />
          )}

          {currentView === "shifts" && (
            <div className={styles.comingSoon}>
              <h2>Shifts</h2>
              <p>Coming soon...</p>
            </div>
          )}

          {currentView === "clients" && (
            <div className={styles.comingSoon}>
              <h2>Clients</h2>
              <p>Coming soon...</p>
            </div>
          )}

          {currentView === "client-messages" && (
            <div className={styles.comingSoon}>
              <h2>Client Messages</h2>
              <p>Coming soon...</p>
            </div>
          )}

          {currentView === "business-accounts" && (
            <div className={styles.comingSoon}>
              <h2>Business Accounts</h2>
              <p>Coming soon...</p>
            </div>
          )}

          {currentView === "pricing" && (
            <div className={styles.comingSoon}>
              <h2>Pricing</h2>
              <p>Coming soon...</p>
            </div>
          )}

          {currentView === "planning" && (
            <div className={styles.comingSoon}>
              <h2>Planning</h2>
              <p>Coming soon...</p>
            </div>
          )}

          {currentView === "reports-overview" && (
            <div className={styles.comingSoon}>
              <h2>Reports - Overview</h2>
              <p>Coming soon...</p>
            </div>
          )}

          {currentView === "reports-drivers" && (
            <div className={styles.comingSoon}>
              <h2>Reports - Drivers</h2>
              <p>Coming soon...</p>
            </div>
          )}

          {currentView === "reports-capacity" && (
            <div className={styles.comingSoon}>
              <h2>Reports - Max Capacity</h2>
              <p>Coming soon...</p>
            </div>
          )}

          {currentView === "reports-customers" && (
            <div className={styles.comingSoon}>
              <h2>Reports - Customers</h2>
              <p>Coming soon...</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}